import { Input } from '@/components/ui/input'
import {
  getDependentChoiceEmptyMessage,
  getFilteredDependentChoices,
} from '@/lib/dependent-choices'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { useMemo, useState } from 'react'
import type { AnswerInputProps } from '../QuestionRenderer'

export function DropdownInput({
  question,
  value,
  answers,
  onChange,
  onSubmit,
  disabled = false,
  loadingMessage = null,
}: AnswerInputProps<string>) {
  const [keyword, setKeyword] = useState('')
  const dependentChoices = useMemo(
    () => getFilteredDependentChoices(question, answers),
    [answers, question],
  )
  const choices = useMemo(
    () =>
      dependentChoices.filter((choice) =>
        choice.label.toLowerCase().includes(keyword.toLowerCase()),
      ),
    [dependentChoices, keyword],
  )
  const hasDependentChoices = dependentChoices.length > 0

  return (
    <div
      className="border p-3"
      style={{
        background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 86%, white)',
        borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
        borderRadius: 'var(--form-radius,8px)',
      }}
    >
      <Input
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder={disabled && loadingMessage ? loadingMessage : '搜尋選項...'}
        disabled={disabled}
        style={{
          borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
          color: 'var(--form-answer,#334155)',
          fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
          fontSize: 'var(--form-body-size,1.125rem)',
        }}
      />
      <div className="mt-3 max-h-64 overflow-y-auto">
        {disabled && loadingMessage ? (
          <div
            role="status"
            aria-label={`${question.title}選項更新中`}
            className="rounded-md border border-dashed border-slate-300 bg-white/70 px-3 py-3 text-sm font-medium text-slate-600"
          >
            {loadingMessage}
          </div>
        ) : null}
        {!hasDependentChoices ? (
          <div className="rounded-md border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-500">
            {getDependentChoiceEmptyMessage(question)}
          </div>
        ) : null}
        {choices.map((choice) => {
          const selected = value === choice.value
          return (
            <button
              key={choice.id}
              type="button"
              aria-pressed={selected}
              data-selected={selected ? 'true' : 'false'}
              disabled={disabled}
              onClick={() => {
                if (disabled) return
                onChange(choice.value)
                onSubmit()
              }}
              className={cn(
                'flex min-h-11 w-full items-center justify-between rounded-md border border-transparent px-3 text-left transition-colors hover:bg-slate-50',
                selected && 'border-l-4 font-semibold shadow-sm',
                disabled && 'cursor-not-allowed opacity-55 hover:bg-transparent',
              )}
              style={{
                background: selected
                  ? 'color-mix(in srgb, var(--form-accent,#6366F1) 16%, transparent)'
                  : 'transparent',
                borderColor: selected ? 'var(--form-accent,#6366F1)' : 'transparent',
                color: selected ? 'var(--form-text,#0F172A)' : 'var(--form-answer,#334155)',
                fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
                fontSize: 'var(--form-body-size,1.125rem)',
              }}
            >
              {choice.label}
              {selected && (
                <Check className="size-4" style={{ color: 'var(--form-accent,#6366F1)' }} />
              )}
            </button>
          )
        })}
        {question.settings.showOther ? (
          <button
            type="button"
            aria-pressed={value === 'other'}
            data-selected={value === 'other' ? 'true' : 'false'}
            disabled={disabled}
            onClick={() => {
              if (disabled) return
              onChange('other')
            }}
            className={cn(
              'flex min-h-11 w-full items-center justify-between rounded-md border border-transparent px-3 text-left transition-colors hover:bg-slate-50',
              value === 'other' && 'border-l-4 font-semibold shadow-sm',
              disabled && 'cursor-not-allowed opacity-55 hover:bg-transparent',
            )}
            style={{
              background:
                value === 'other'
                  ? 'color-mix(in srgb, var(--form-accent,#6366F1) 16%, transparent)'
                  : 'transparent',
              borderColor: value === 'other' ? 'var(--form-accent,#6366F1)' : 'transparent',
              color: value === 'other' ? 'var(--form-text,#0F172A)' : 'var(--form-answer,#334155)',
              fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
              fontSize: 'var(--form-body-size,1.125rem)',
            }}
          >
            其他
            {value === 'other' && (
              <Check className="size-4" style={{ color: 'var(--form-accent,#6366F1)' }} />
            )}
          </button>
        ) : null}
      </div>
      {question.settings.randomize ? (
        <p className="mt-2 text-xs text-slate-500">公開填答時會依設定隨機排列選項。</p>
      ) : null}
    </div>
  )
}
